import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Loading } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useCancelRuleTest from 'Hooks/query-hooks/global-rules/useCancelRuleTest';
import useGetRuleTestResults from 'Hooks/query-hooks/global-rules/useGetRuleTestResults';
import { DateService, NumberService } from 'Services';
import useTemplateConfig from 'Hooks/query-hooks/useTemplateConfig';
import clsx from 'clsx';
import { ICON, RT_HEADERS, STATUS } from 'Components/AddRulePage/RuleTester/constants';
import RuleStatement from '../RuleStatement';
import Card from '../Card';

import styles from './Results.css';

type ResponseStatus = {
    [key: string]: ReactNode;
};

type Props = {
    ruleId: string;
    ruleTestId: string | null;
    parameterData?: UseGetParametersResponse;
    listData?: ListApiGetResponse;
    conclusionFlagListData?: ConclusionListApiGetResponse[];
    mode: 'add' | 'edit' | 'read';
    setIsRuleTestRunning: Function;
    merchantId: string;
    tenantGroupId: string;
    ruleSetId: string;
    overrideLevel?: OverrideLevel;
    isRuleTestRunning: boolean;
};

const Results = ({
    ruleId,
    ruleTestId,
    parameterData,
    listData,
    conclusionFlagListData,
    mode,
    setIsRuleTestRunning,
    merchantId,
    tenantGroupId,
    ruleSetId,
    overrideLevel,
    isRuleTestRunning
}: Props) => {
    const { t } = useTranslation();
    const templateConfig = useTemplateConfig();
    const { addTechnicalErrorModal, clearAll } = useReduxModal();
    const [shouldPoll, setShouldPoll] = useState(false);

    const { data: ruleTestResults, refetch: refetchStatus } = useGetRuleTestResults({
        ruleId,
        shouldPoll,
        mode,
        ruleTestId,
        merchantId,
        tenantGroupId,
        ruleSetId
    });
    const cancelRuleTest = useCancelRuleTest({
        ruleId,
        ruleTestId,
        refetchStatus,
        merchantId,
        tenantGroupId,
        ruleSetId
    });

    const resultsData =
        {
            ...ruleTestResults,
            createdAt: DateService.getDateTime(ruleTestResults?.createdAt) ?? ruleTestResults?.createdAt,
            startDt: DateService.getDateTime(ruleTestResults?.startDt) ?? ruleTestResults?.startDt,
            endDt: DateService.getDateTime(ruleTestResults?.endDt) ?? ruleTestResults?.endDt
        } || {};

    const { status, matchEventRecords, totalRecords, sumOfMatchEventAmount, totalAmount } = resultsData;

    useEffect(() => {
        if (status === 'In Progress') {
            setShouldPoll(true);
            setIsRuleTestRunning(true);
        } else if (status === 'Failed' || status === 'FINISHED' || status === 'CANCELLED') {
            setShouldPoll(false);
            setIsRuleTestRunning(false);
        }
    }, [ruleTestId, status, ruleId]);

    const handleTrackEvent = () => {
        let clickIdentifier = '';
        let text = '';
        if (mode === 'add') {
            clickIdentifier = 'New Simple Rule - Cancel Rule Test';
            text = 'Cancel New Simple Global Rule Test';
            if (overrideLevel === 'tenantGroup') {
                clickIdentifier = 'New Tenant Group Override - Cancel Rule Test';
                text = 'Cancel New Tenant Group Override Rule Test';
            } else if (overrideLevel === 'merchant') {
                clickIdentifier = 'New Subject Override - Cancel Rule Test';
                text = 'Cancel New Subject Override Rule Test';
            }
        } else if (mode === 'edit') {
            clickIdentifier = 'Edit Simple Rule - Cancel Rule Test';
            text = 'Cancel Global Rule Test';
            if (overrideLevel === 'tenantGroup') {
                clickIdentifier = 'Edit Tenant Group Override - Cancel Rule Test';
                text = 'Cancel Tenant Group Override Rule Test';
            } else if (overrideLevel === 'merchant') {
                clickIdentifier = 'Edit Subject Override - Cancel Rule Test';
                text = 'Cancel Subject Override Rule Test';
            }
        }
        if (!clickIdentifier || !text) {
            return;
        }
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const handleCancelRuleTest = () => {
        addTechnicalErrorModal('', t('Cancel Rule Test'), clearAll, {
            status: 'warning',
            content: (
                <>
                    <div style={{ marginTop: '24px' }}>{t('cancelRuleTest')}</div>
                </>
            ),
            onActionText: t('Cancel Rule Test'),
            onAction: () => {
                cancelRuleTest.mutate();
                clearAll();
                setIsRuleTestRunning(false);
                handleTrackEvent();
            },
            cancelText: t('Close')
        });
    };

    const noResult = <div className={styles.noResult}>{t('No rule test results available')}</div>;

    const resultStatus: ResponseStatus = {
        Failed: noResult,
        CANCELLED: noResult,
        FINISHED: (
            <div className={styles.ruleTesterResults}>
                <Card
                    cardLabel={{
                        key: 'Matched Count %',
                        value:
                            (Number(matchEventRecords) &&
                                Number(totalRecords) &&
                                `${((Number(matchEventRecords) / Number(totalRecords)) * 100).toFixed(2)}%`) ||
                            '0%'
                    }}
                    cardDesc={{
                        key: 'Matched Count',
                        value: (Number(matchEventRecords) && `${Number(matchEventRecords).toLocaleString()}`) || '0'
                    }}
                    cardFooter={{
                        key: 'Total Events',
                        value: (Number(totalRecords) && `${Number(totalRecords).toLocaleString()}`) || '0'
                    }}
                />
                <Card
                    cardLabel={{
                        key: 'Matched Value',
                        value:
                            (Number(sumOfMatchEventAmount) &&
                                `${NumberService.getLocaleCurrency(Number(sumOfMatchEventAmount)).toLocaleString()}`) ||
                            NumberService.getLocaleCurrency(0)
                    }}
                    cardDesc={{
                        key: 'Total Value',
                        value:
                            (Number(totalAmount) &&
                                `${NumberService.getLocaleCurrency(Number(totalAmount)).toLocaleString()}`) ||
                            NumberService.getLocaleCurrency(0)
                    }}
                    cardFooter={{
                        key: 'Matched Value %',
                        value:
                            (Number(sumOfMatchEventAmount) &&
                                Number(totalAmount) &&
                                `${((Number(sumOfMatchEventAmount) / Number(totalAmount)) * 100).toFixed(2)}%`) ||
                            '0%'
                    }}
                />

                <div className={styles.ruleStatement}>
                    <p className={styles.ruleResultsHeader}>{t('Rule Statement')}</p>
                    <RuleStatement
                        parameterData={parameterData}
                        listData={listData}
                        conclusionFlagListData={conclusionFlagListData}
                        conditions={resultsData.conditions || [{}]}
                        actions={resultsData.actions || [{}]}
                    />
                </div>
            </div>
        )
    };

    if (!status) {
        return noResult;
    }

    return (
        <>
            {isRuleTestRunning ? (
                <div className={styles.cancelRuleTest}>
                    <Loading size='lg' />
                    {mode !== 'read' ? (
                        <Button variant='tertiary' size='medium' onClick={handleCancelRuleTest}>
                            {t('Cancel Rule Test')}
                        </Button>
                    ) : null}
                </div>
            ) : (
                <>
                    {status && <hr />}
                    <p className={styles.ruleResults}>{t('Rule Test Result')}</p>
                    <p>{t('All amounts are in ') + templateConfig?.data?.currencyCode ?? 'USD'}</p>
                    <div className={styles.resultsHeader}>
                        {Object.keys(RT_HEADERS).map((header) => {
                            return (
                                <p key={header}>
                                    {t(`${RT_HEADERS[header]}`)}:
                                    <b>
                                        <span
                                            className={clsx(
                                                styles.iconWrapper,
                                                resultsData[header] === 'In Progress' && styles.impIconWrapper
                                            )}
                                        >
                                            {STATUS[resultsData[header]] && (
                                                <Icon name={ICON[resultsData[header]]} size='md' />
                                            )}
                                        </span>

                                        {STATUS[resultsData[header]] ?? resultsData[header]}
                                    </b>
                                </p>
                            );
                        })}
                    </div>
                    {resultStatus[status]}
                </>
            )}
        </>
    );
};

export default Results;
